import { useCallback, useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { addMinutes } from '@src/utils/dateUtils';

interface CacheItem<T> {
  expiryDate: Date;
  data: T[];
}

const cacheEntryTtlMins = 10;
const infoCache: { [key: string]: CacheItem<any> } = {};
const debounceDelay = 300;

const useCachedList = <T extends unknown>(
  cacheKey: string,
  useLazyFetchHook: () => {
    execute: () => Promise<{ items: T[] } | undefined>;
  },
  ttlMinutes: number = cacheEntryTtlMins
) => {
  const { execute } = useLazyFetchHook();
  const [firstCall, setFirstCall] = useState(true);

  const debouncedExecuteRef = useRef(debounce(execute, debounceDelay));

  useEffect(() => {
    debouncedExecuteRef.current = debounce(execute, debounceDelay);
  }, [execute]);

  const getList = useCallback(
    async (id?: string, refresh?: boolean): Promise<T[]> => {
      const refreshCache = async () => {
        const result = await (firstCall
          ? execute()
          : debouncedExecuteRef.current());

        if (!result || !result.items) {
          return [];
        }

        const data = result.items;
        infoCache[cacheKey] = {
          expiryDate: addMinutes(new Date(), ttlMinutes)!,
          data,
        };

        return data;
      };

      const cachedItem = infoCache[cacheKey] as CacheItem<T>;
      if (
        cachedItem &&
        cachedItem.expiryDate &&
        new Date(cachedItem.expiryDate).getTime() > new Date().getTime() &&
        !refresh
      ) {
        if (id) {
          const foundItem = cachedItem.data.find((d: any) => d.id === id);
          if (foundItem) {
            return [foundItem];
          } else {
            return refreshCache();
          }
        }

        return Promise.resolve(cachedItem.data);
      } else {
        const data = refreshCache();
        setFirstCall(false);

        return data;
      }
    },
    [cacheKey, execute, firstCall, ttlMinutes]
  );

  return {
    getList,
  };
};

export default useCachedList;
